import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Icon } from './Icon';

/**
 * Blocked work, treated so it is unmistakable.
 *
 * Blocked is a flag, not a lane - a task can be blocked *during Build* - so it
 * has to read at a glance without stealing the lane's own colour. Three
 * signals, any one of which is sufficient on its own:
 *
 *   1. a diagonal hatch over the surface (`.hatch-blocked`),
 *   2. a warning triangle,
 *   3. the word "Blocked", and the reason when there is one.
 *
 * Colour is the fourth signal, never the only one. That is the requirement for
 * anyone with a colour vision deficiency, and it also survives a printed
 * screenshot pasted into a status email, which is how these actually travel.
 */
export function BlockedBadge({ reason, className }: { reason?: string | null; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full border border-danger/40 bg-danger-soft px-2.5 py-0.5',
        'text-micro font-semibold text-danger-ink',
        className,
      )}
      title={reason ?? undefined}
    >
      <Icon name="alert" size={13} />
      <span className="truncate">{reason ? `Blocked · ${reason}` : 'Blocked'}</span>
    </span>
  );
}

/**
 * Wraps a card so the whole surface carries the hatch. Use on anything that
 * represents one blocked task; use `BlockedBadge` alone inside a dense table.
 */
export function BlockedSurface({
  blocked,
  children,
  className,
}: {
  blocked: boolean;
  children: ReactNode;
  className?: string;
}) {
  if (!blocked) return <div className={className}>{children}</div>;

  return (
    <div className={cn('relative isolate', className)}>
      <span
        className="hatch-blocked pointer-events-none absolute inset-0 z-10 rounded-lg"
        aria-hidden="true"
      />
      {children}
    </div>
  );
}
